import { Component, EventEmitter, Input, Output } from '@angular/core';
import { OpeningTimeRange, TimeOfDay } from '../../../model/opening-hours';
import {
  OpeningHourUtil,
  MAX_TIME_SLOTS,
} from '../../../util/opening-hour-util';

export type OpeningTimeKind = 'not-available' | 'closed' | 'open24' | 'ranges';

export type OpeningTimeValue =
  | { kind: 'not-available' }
  | { kind: 'closed' }
  | { kind: 'open24' }
  | { kind: 'ranges'; ranges: OpeningTimeRange[] };

@Component({
  selector: 'gymfinder-opening-time-editor',
  templateUrl: './opening-time-editor.component.html',
  styleUrls: ['./opening-time-editor.component.scss'],
  standalone: false,
})
export class OpeningTimeEditorComponent {
  @Input()
  editMode: boolean;

  /** Whether to show the N/A option (regular days only, not exceptions). */
  @Input()
  showNotAvailable = false;

  /** Maximum number of time slots that can be added. */
  @Input()
  maxTimeSlots = MAX_TIME_SLOTS;

  @Input()
  set value(val: OpeningTimeValue) {
    this.selectedKind = val.kind;
    if (val.kind === 'ranges') {
      this.timeRanges = val.ranges.map(r => ({ ...r }));
    }
  }

  @Output()
  valueChange = new EventEmitter<OpeningTimeValue>();

  selectedKind: OpeningTimeKind = 'not-available';
  timeRanges: OpeningTimeRange[] = [];

  onKindChange(kind: OpeningTimeKind) {
    this.selectedKind = kind;
    if (kind === 'ranges' && this.timeRanges.length === 0) {
      this.timeRanges = [this.emptyRange()];
    }
    if (kind === 'ranges') {
      this.emitRanges();
    } else {
      this.valueChange.emit({ kind });
    }
  }

  onTimeChange(value: TimeOfDay, type: 'start' | 'end', index: number) {
    this.timeRanges[index] = { ...this.timeRanges[index], [type]: value };
    this.emitRanges();
  }

  get canAddTimeSlot(): boolean {
    return this.timeRanges.length < this.maxTimeSlots;
  }

  addSlot() {
    if (!this.canAddTimeSlot) {
      return;
    }
    this.timeRanges.push(this.emptyRange());
    this.emitRanges();
  }

  removeSlot(index: number) {
    this.timeRanges.splice(index, 1);
    if (this.timeRanges.length === 0) {
      this.selectedKind = 'closed';
      this.valueChange.emit({ kind: 'closed' });
    } else {
      this.emitRanges();
    }
  }

  getSlotErrors(index: number): string[] {
    return OpeningHourUtil.getSlotErrors(this.timeRanges, index);
  }

  get hasOverlapOrTouch(): boolean {
    return OpeningHourUtil.hasOverlapOrTouch(this.timeRanges);
  }

  private emitRanges() {
    this.valueChange.emit({ kind: 'ranges', ranges: [...this.timeRanges] });
  }

  private emptyRange(): OpeningTimeRange {
    return {
      start: { hour: 8, minute: 0 },
      end: { hour: 22, minute: 0 },
    };
  }
}
